import { Inject } from '@odinjs/odin';
import moment from 'moment';

import { MountEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import Controller, { OnBeforeCancel, OnBeforeSubmit, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { AutocompleteAPI, FetchAPI, FieldAPI, FormAPI, InputAPI, TextAreaAPI } from '@philips-internal/oswald-ui-components';
import { applicationContext, foundationContext } from '@philips-internal/oswald-ui-foundation';
import { Value } from '@philips-internal/oswald-ui-core';
import { HttpService, MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { ToastLevel } from '@philips-internal/oswald-ui-constants/lib';

import { FragmentNames, ReservedBloodProductsSuspendAliases } from '../../../../../codegen';
import { ReasonType, ReservedProduction } from '../../blood-management/blood-management-types';
import BaseController from '../../../../BaseController';
import { ISO_FULL_DATE_FORMAT } from '../../../../../constants/date-utils';
import { PrescriptionType, solutionMapping } from '../../../services/timeline-service';
import { AdminModalsService } from '../../../services/admin-modals-service';
import { ApplicationRoute } from '../../../../../constants/routes';
import MedicationService from '../../../services/medication-service';
import { Endpoint } from '../../../../../constants/endpoints';

@Controller({ component: ReservedBloodProductsSuspendAliases.RESERVED_BLOOD_PRODUCTS_SUSPEND_LAYOUT, fragment: FragmentNames.RESERVED_BLOOD_PRODUCTS_SUSPEND })
export default class ReservedBloodProductsSuspendController extends BaseController {

  @Inject(ReservedBloodProductsSuspendAliases.RESERVED_BLOOD_PRODUCTS_SUSPEND_FORM)
  private suspendFormApi!: FormAPI

  @Inject(ReservedBloodProductsSuspendAliases.SCHEDULE_DATE_INPUT)
  private scheduleDateInputApi!: InputAPI;

  @Inject(ReservedBloodProductsSuspendAliases.TITLE_FIELD)
  private titleFieldApi!: FieldAPI;

  @Inject(ReservedBloodProductsSuspendAliases.REASON_AUTOCOMPLETE)
  private reasonAutocompleteAPI!: AutocompleteAPI;

  @Inject(ReservedBloodProductsSuspendAliases.NOTES_TEXT_AREA)
  private notesJustificationTextAreaApi!: TextAreaAPI;

  @OnMount(ReservedBloodProductsSuspendAliases.RESERVED_BLOOD_PRODUCTS_SUSPEND_LAYOUT)
  handleLayoutInterruptMount(): void {
    const reservedProduct = this.context.getBulkData() as unknown as ReservedProduction;
    this.context.set({
      reservedProduct: { ...reservedProduct },
    });
    this.setDataPanelTitle(reservedProduct);
  }

  setDataPanelTitle(reservedProduct: ReservedProduction): void {
    this.scheduleDateInputApi.setValue(moment(reservedProduct.reservationDate).utc().format(ISO_FULL_DATE_FORMAT));
    this.scheduleDateInputApi.setReadOnly(true);
    this.titleFieldApi.setData({ materialDescription: reservedProduct.description });
  }

  @OnBeforeSubmit(ReservedBloodProductsSuspendAliases.RESERVED_BLOOD_PRODUCTS_SUSPEND_FORM)
  handleBeforeSubmit(): void {
    this.suspendFormApi.validate();
    const body = this.getRequestBody();
    this.generatePresciptionChangesInterrupt(body)
  }

  @OnBeforeCancel(ReservedBloodProductsSuspendAliases.RESERVED_BLOOD_PRODUCTS_SUSPEND_FORM)
  handleCancelForm(): void {
    applicationContext.popNavigationItem();
  }

  @OnMount(ReservedBloodProductsSuspendAliases.REASON_FETCH)
  handleFetchReasonMount(event: MountEvent): void {
    const fetchReason = event.target as FetchAPI;
    const establishmentCode = foundationContext.getEstablishment()?.id;
    fetchReason.setParameters({ establishmentCode, status: PrescriptionType.BLOOD_MANAGEMENT }, { skip: true });
  }

  private getRequestBody(): Record<string, Value> {
    const reservedProduct = this.context.get('reservedProduct');
    const { encounterId } = this.historyService.getCurrentRouteParams();
    const reasonSelected = this.reasonAutocompleteAPI.getValue() as unknown as ReasonType;
    const changeTypeSuspend = 53;

    return {
      bagNumber: reservedProduct.bagNumber,
      changeDate: moment().format(ISO_FULL_DATE_FORMAT),
      changeType: changeTypeSuspend,
      dateTime: null,
      encounterNumber: encounterId as number,
      justificationDescription: this.notesJustificationTextAreaApi.getValue() as string ?? reasonSelected.reasonDescription,
      prescriptionNumber: reservedProduct.prescriptionNumber,
      reasonSequenceNumber: reasonSelected.sequenceNumber,
      reservationProductSequenceNumber: reservedProduct.id,
      reservationSequenceNumber: reservedProduct.reservationId,
      solutionSequenceNumber: reservedProduct.materialSequence ?? 0,
      solutionType: solutionMapping[PrescriptionType.BLOOD_MANAGEMENT],
    }
  }

  private generatePresciptionChangesInterrupt(body: Record<string, Value>): void {
    HttpService.post(Endpoint.ADEP_GENERATE_CHANGE_SOLUCAO_PRESCR, {}, { body })
      .then(response => {
        const responseBody = response.body as { messageDescription: string };
        if (responseBody) {
          this.showResponseMessage(responseBody?.messageDescription);
          this.redirectToTimeline();
        }
      }).catch((error) => {
        if (error.body && error.body.errors) {
          error.body.errors.forEach((message: string) => MedicationService.showToast(MessageService.translate(message), ToastLevel.ERROR));
        }
      })
  }

  private showResponseMessage(message: string): void {
    if (message) {
      MedicationService.showToast(message, ToastLevel.ERROR);
      return
    }
    MedicationService.showToast(MessageService.translate('txt.emar.item-suspend'), ToastLevel.SUCCESS);
  }

  private redirectToTimeline(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    const redirectRoute = ApplicationRoute.ADEP_ELETRONIC_SERVICE_PERSONAL_INFORMATION;
    applicationContext.redirect(redirectRoute, { encounterId, patientId });
    applicationContext.clearNavigationStack();
    AdminModalsService.redirectToTimeline(FragmentNames.RESERVED_BLOOD_PRODUCTS_SUSPEND);
    AdminModalsService.redirectToTimeline(FragmentNames.RESERVED_BLOOD_PRODUCTS_VIEWER);
  }
}
